import React, { useEffect, useState, useRef, useMemo, useCallback, memo } from "react";
import ReactSelect from "react-select";
import axiosInstance from "../../../../../redux/api";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Formik, ErrorMessage, Form } from "formik";
import * as Yup from "yup";
import {
  getSkillswithCategory,
  getIndustry,
} from "../../../../../redux/categary/action";
import { titlevalidation } from "../../../../Common/commonvalidation";
import ConfirmModal from "../../../../Common/ConfirmModal";
import AsteriskLabel from "../../../../Common/AsteriskLabel";

const AddCategorySubcategory = memo(({
  formData,
  setFormData,
  validationForCategory,
}) => {
  const dispatch = useDispatch();

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState({});

  // Modal states
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [modalConfig, setModalConfig] = useState({});

  const industrylist = useSelector(
    ({ categary: { industrylist } }) => industrylist,
    shallowEqual
  );

  // Memoize industry options
  const industryOptions = useMemo(() => {
    return Array.isArray(industrylist)
      ? industrylist.map((i) => ({
        value: i.id,
        label: i.title,
      }))
      : [];
  }, [industrylist]);

  // Memoize selected industry values
  const selectedIndustryValues = useMemo(() => {
    return (formData?.industries || []).map((i) => ({
      value: i.id,
      label: i.title,
    }));
  }, [formData?.industries]);

  // Memoize selected category values
  const selectedCategoryValues = useMemo(() => {
    return (formData?.categories || []).map((c) => ({
      value: c.id,
      label: c.title,
    }));
  }, [formData?.categories]);

  // Helper for subcategory values
  const getSubcategoryValue = (subCategories) => {
    if (!Array.isArray(subCategories)) return [];
    return subCategories.map((s) => ({
      value: s.id,
      label: s.title,
    }));
  };

  // Validation Schema
  const validationSchema = useMemo(() => Yup.object().shape({
    industries: Yup.array().min(1, "Industry is required"),
    categories: Yup.array().of(
      Yup.object().shape({
        title: titlevalidation,
        sub_categories: Yup.array().min(1, "At least one subcategory is required"),
      })
    ),
  }), []);

  // Fetch industries initially
  useEffect(() => {
    dispatch(getIndustry());
  }, [dispatch]);

  const isInitialLoad = useRef(true);

  const fetchSubcategories = useCallback(async (parentId) => {
    try {
      const res = await axiosInstance.get(`category?type=3&parent_id=${parentId}`);
      const options = res.data.response.map((sub) => ({
        value: sub.id,
        label: sub.title,
      }));
      setSubcategoryOptions((prev) => ({ ...prev, [parentId]: options }));
    } catch (err) {
      console.error("Error fetching subcategories:", err);
    }
  }, []);

  const fetchCategories = useCallback(async (industryIds) => {
    if (!industryIds.length) {
      setCategoryOptions([]);
      return;
    }
    try {
      const allCategories = [];
      for (const id of industryIds) {
        const res = await axiosInstance.get(`category?type=2&parent_id=${id}`);
        const options = res.data.response.map((cat) => ({
          value: cat.id,
          label: cat.title,
        }));
        allCategories.push(...options);
      }
      setCategoryOptions(allCategories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategoryOptions([]);
    }
  }, []);

  // Restore options on mount
  useEffect(() => {
    if (isInitialLoad.current && formData?.industries?.length > 0) {
      isInitialLoad.current = false;
      fetchCategories(formData.industries.map((i) => i.id)).then(() => {
        if (formData?.categories?.length > 0) {
          formData.categories.forEach((cat) => {
            fetchSubcategories(cat.id);
          });
        }
      });
    }
  }, [fetchCategories, fetchSubcategories]);

  // Modal handlers
  const handleConfirm = useCallback(() => {
    if (pendingAction) pendingAction();
    setConfirmModalOpen(false);
  }, [pendingAction]);

  const handleCancel = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  // Industry change
  const handleIndustryChange = useCallback((options) => {
    const newIndustries = options?.map((opt) => ({
      id: opt.value,
      title: opt.label,
    })) || [];

    const removed = (formData?.industries || []).filter(
      industry => !newIndustries.some(ni => ni.id === industry.id)
    );

    const proceed = () => {
      setFormData(prev => ({
        ...prev,
        industries: newIndustries,
        categories: [],
        skills: []
      }));
      setCategoryOptions([]);
      setSubcategoryOptions({});
      if (newIndustries.length > 0) {
        fetchCategories(newIndustries.map(i => i.id));
      }
    };

    if (removed.length > 0) {
      setPendingAction(() => proceed);
      setModalConfig({
        title: "Remove Industry",
        message: "Removing an industry will clear associated categories and skills.",
        itemsToRemove: removed.map(i => i.title)
      });
      setConfirmModalOpen(true);
    } else {
      proceed();
    }
  }, [formData?.industries, setFormData, fetchCategories]);

  // Category change
  const handleCategoryChange = useCallback((options) => {
    const newCategories = options?.map((opt) => {
      fetchSubcategories(opt.value);
      return {
        id: opt.value,
        title: opt.label,
        sub_categories: [],
      };
    }) || [];

    const removed = (formData?.categories || []).filter(
      cat => !newCategories.some(nc => nc.id === cat.id)
    );

    const proceed = () => {
      setFormData(prev => ({
        ...prev,
        categories: newCategories,
        skills: []
      }));
    };

    if (removed.length > 0) {
      setPendingAction(() => proceed);
      setModalConfig({
        title: "Remove Category",
        message: "Removing a category will clear associated subcategories and skills.",
        itemsToRemove: removed.map(c => c.title)
      });
      setConfirmModalOpen(true);
    } else {
      proceed();
    }
  }, [formData?.categories, setFormData, fetchSubcategories]);

  // Subcategory change
  const handleSubcategoryChange = useCallback((options, index) => {
    setFormData(prev => {
      const updated = [...(prev.categories || [])];
      if (updated[index]) {
        updated[index].sub_categories = options.map(s => ({ id: s.value, title: s.label }));
      }
      return { ...prev, categories: updated, skills: [] };
    });
  }, [setFormData]);

  // Skills dispatch
  const prevCategoryIdsRef = useRef("");
  useEffect(() => {
    const currentIds = (formData?.categories || []).map((c) => c.id).sort().join(",");
    if (currentIds !== prevCategoryIdsRef.current) {
      prevCategoryIdsRef.current = currentIds;
      dispatch(getSkillswithCategory(currentIds || "-1"));
    }
  }, [formData?.categories, dispatch]);

  return (
    <>
      <div className="skill-sec">
        <Formik
          enableReinitialize
          initialValues={useMemo(() => ({
            industries: formData?.industries || [],
            categories: formData?.categories || [],
          }), [formData?.industries, formData?.categories])}
          validationSchema={validationSchema}
          onSubmit={() => { }}
        >
          <Form>
            <div className="form-group">
              <label>Select Industries <AsteriskLabel /></label>
              <ReactSelect
                isMulti
                options={industryOptions}
                value={selectedIndustryValues}
                onChange={handleIndustryChange}
                placeholder="Select Industries"
              />
              <ErrorMessage name="industries" component="div" className="error" />
            </div>

            {formData?.industries?.length > 0 && (
              <div className="form-group">
                <label>Select Categories <AsteriskLabel /></label>
                <ReactSelect
                  isMulti
                  options={categoryOptions}
                  value={selectedCategoryValues}
                  onChange={handleCategoryChange}
                  placeholder="Select Categories"
                />
                <ErrorMessage name="categories" component="div" className="error" />
              </div>
            )}

            {(formData?.categories || []).map((cat, idx) => (
              <React.Fragment key={cat.id}>
                {(Array.isArray(subcategoryOptions[cat.id]) && subcategoryOptions[cat.id]?.length > 0) && (
                  <div className="row modal-edit-row mb-2 align-items-center">
                    <div className="col-md-4">
                      <label className="mb-0">{cat.title}</label>
                    </div>
                    <div className="col-md-8">
                      <ReactSelect
                        isMulti
                        options={subcategoryOptions[cat.id] || []}
                        value={getSubcategoryValue(cat.sub_categories)}
                        placeholder="Select Subcategories"
                        onChange={(opts) => handleSubcategoryChange(opts, idx)}
                      />
                      <ErrorMessage
                        name={`categories[${idx}].sub_categories`}
                        component="div"
                        className="error"
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </Form>
        </Formik>

        {validationForCategory && (
          <p style={{ color: "red" }}>
            Please select at least 1 industry and 1 category
          </p>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModalOpen}
        toggle={() => setConfirmModalOpen(false)}
        title={modalConfig.title || "Confirm Action"}
        message={modalConfig.message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        itemsToRemove={modalConfig.itemsToRemove}
      />
    </>
  );
});

export default AddCategorySubcategory;